import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';

import { makePortalUserNotificationsChannel, SocketEvent } from '@furality/centrifugo-types';
import { ReportError, useAuth, useQuery } from '@furality/frontend-stack';
import fox from '#/api/fox.ts';
import DealersView, { NO_BOOTH, useActiveBooth } from '@/components/_dealers/DealersView.tsx';
import ExplorePage from '@/components/_explore/ExplorePage.tsx';
import { DealersLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import useChannelEventSubscription from '@/lib/hooks/useChannelEventSubscription';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';

const Dealers = () => {
  const featuresReady = useFlagsStatus().flagsReady;
  const enableDealers = useFlag('dealersDen');
  const auth = useAuth();

  const dealers = useQuery({
    queryKey: [QUERY_KEYS.Dealers],
    queryFn: async () => {
      const response = await fox.value.dealerFindAll();
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
    enabled: enableDealers,
  });

  useChannelEventSubscription(
    makePortalUserNotificationsChannel(auth.user?.id ?? ''),
    SocketEvent.DealerBookmarkUpdated,
    () => {
      dealers.refetch();
    },
    { enabled: !!auth.user?.id },
  );

  useTranslation(['common', 'dealers']);

  const activeBooth = useActiveBooth(dealers.data?.data);

  const hasBoothOpen = typeof activeBooth === 'number';

  if (!featuresReady) {
    return <PageLoading />;
  }
  if (!enableDealers) {
    return <PageDisabled absolutelyCentered />;
  }

  return (
    <>
      {dealers.isLoading && <PageLoading />}

      <ExplorePage
        isSubPage
        hideHeader={hasBoothOpen}
        hideScrollToTopButton={!hasBoothOpen}
        title="explore:dealers"
      >
        {dealers.isSuccess && <DealersView dealers={dealers.data.data} />}
      </ExplorePage>
    </>
  );
};

const DealersSearch = Type.Object({
  id: Type.Optional(Type.Number({ default: NO_BOOTH })),
  locate: Type.Optional(Type.Number({ default: NO_BOOTH })),
});
export const Route = createFileRoute('/explore/dealers')({
  component: () => (
    <DealersLayout title="explore:dealers">
      <Dealers />
    </DealersLayout>
  ),
  validateSearch: (search) => Value.Cast(DealersSearch, search),
});
